import React, { useMemo, useEffect, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from "recharts";
import ChartSummaryRows from "./ChartSummaryRows";
import { RISK_CHART_COLORS } from "../../utils/actionCardColors";

const COLOR_DEFAULT = RISK_CHART_COLORS.default;
const COLOR_TRAINING = RISK_CHART_COLORS.training;
const COLOR_READING = RISK_CHART_COLORS.reading;
const COLOR_YELLOW = RISK_CHART_COLORS.yellow;
const COLOR_RED = RISK_CHART_COLORS.red;
const COLOR_NEAR_MISS = RISK_CHART_COLORS.nearMiss;
const COLOR_PREVENTION = RISK_CHART_COLORS.prevention;
const COLOR_DOUBT = RISK_CHART_COLORS.doubt;
const BAR_VALUE_LABEL_STYLE = {
  fontSize: 14,
  fontWeight: 700,
  fill: "#111",
};

const REPORTING_CATEGORY_SERIES = [
  {
    key: "nearMiss",
    fieldName: "has_never_miss",
    color: COLOR_NEAR_MISS,
    labels: { en: "Near miss", dk: "Near miss" },
  },
  {
    key: "prevention",
    fieldName: "has_prevention",
    color: COLOR_PREVENTION,
    labels: { en: "Prevention", dk: "Forebyggelse" },
  },
  {
    key: "doubtAboutReporting",
    fieldName: "has_doubt_about_reporting",
    color: COLOR_DOUBT,
    labels: { en: "Doubt about reporting", dk: "Tvivl om rapportering" },
  },
];

const makeTooltip = ({ rows, usePayloadValues = false }) => {
  const Square = ({ color }) => (
    <span
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        background: color,
        marginRight: 8,
        border: "1px solid #ccc",
      }}
    />
  );

  return ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const rowsToRender = usePayloadValues
      ? payload
          .filter((p) => p && p.dataKey)
          .map((p) => ({ label: p.name, value: p.value, color: p.fill }))
      : rows;

    if (!rowsToRender?.length) return null;
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #999",
          borderRadius: 4,
          padding: 10,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          fontSize: 13,
          color: "#222",
          minWidth: 170,
        }}
      >
        {rowsToRender.map((row, idx) => (
          <div
            key={row.label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: idx === rows.length - 1 ? 0 : 4,
            }}
          >
            <Square color={row.color} />
            <span>
              {row.label}: {row.value}
            </span>
          </div>
        ))}
      </div>
    );
  };
};

// Pure helper — not a hook, safe to define outside component
const buildCounts = (records) => {
  const map = {};
  records.forEach((rec) => {
    const acNo = String(rec.action_card_number ?? "").trim();
    if (!acNo) return;
    map[acNo] = (map[acNo] ?? 0) + 1;
  });
  return map;
};

const normalizeIncidentType = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "null" ? "" : normalized;
};

const getIncidentTypeValue = (record) =>
  normalizeIncidentType(
    record?.real_incident_type || record?.read_incident_type,
  );

const getIncidentTypeCount = (record) => {
  const parsed = Number(record?.real_incident_type_count);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return getIncidentTypeValue(record) ? 1 : 0;
};

const buildIncidentTypeCounts = (records, targetType) => {
  const normalizedTarget = normalizeIncidentType(targetType);
  const map = {};

  records.forEach((rec) => {
    if (getIncidentTypeValue(rec) !== normalizedTarget) {
      return;
    }

    const acNo = String(rec.action_card_number ?? "").trim();
    if (!acNo) return;

    map[acNo] = (map[acNo] ?? 0) + getIncidentTypeCount(rec);
  });

  return map;
};

const isTruthyFlag = (value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;

  const normalized = String(value).trim().toLowerCase();
  return normalized === "1" || normalized === "true";
};

const buildReportingCategoryCounts = (records, fieldName) => {
  const map = {};

  records.forEach((rec) => {
    if (!isTruthyFlag(rec?.[fieldName])) {
      return;
    }

    const acNo = String(rec.action_card_number ?? "").trim();
    if (!acNo) return;

    map[acNo] = (map[acNo] ?? 0) + 1;
  });

  return map;
};

const RiskBarChart = ({
  defaultRecords = [],
  reportingCategoryRecords = [],
  trainingRecords = [],
  readRecords = [],
  showTraining = false,
  showRead = false,
  actionCards = [],
  lang = "en",
  organizationId,
  showIncidentYellow = true,
  showIncidentRed = true,
  showNearMiss = false,
  showPrevention = false,
  showDoubtAboutReporting = false,
  height = 640,
  onDataUpdate,
}) => {
  // ── Every hook called unconditionally at the top ──────────────────────────
  const cbRef = useRef(onDataUpdate);

  useEffect(() => {
    cbRef.current = onDataUpdate;
  }, [onDataUpdate]);

  const uniqueActionCards = useMemo(() => {
    const seen = new Set();
    return actionCards.filter((card) => {
      const key = String(card?.ac_no ?? "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [actionCards]);

  const isGentofteLife = Number(organizationId) === 19;
  const hasIncidentTypeFilter = showIncidentYellow || showIncidentRed;
  const hasReportingCategoryFilter =
    showNearMiss || showPrevention || showDoubtAboutReporting;
  const showIncidentTypeBars = isGentofteLife && hasIncidentTypeFilter;
  const showReportingCategoryBars = hasReportingCategoryFilter;
  const hideDefaultBar = showIncidentTypeBars || showReportingCategoryBars;
  const reportingCategoryVisibility = useMemo(
    () => ({
      nearMiss: showNearMiss,
      prevention: showPrevention,
      doubtAboutReporting: showDoubtAboutReporting,
    }),
    [showNearMiss, showPrevention, showDoubtAboutReporting],
  );

  const defaultCounts = useMemo(
    () => buildCounts(defaultRecords),
    [defaultRecords],
  );

  const yellowCounts = useMemo(
    () => buildIncidentTypeCounts(defaultRecords, "yellow"),
    [defaultRecords],
  );

  const redCounts = useMemo(
    () => buildIncidentTypeCounts(defaultRecords, "red"),
    [defaultRecords],
  );

  const reportingCategoryCounts = useMemo(
    () =>
      REPORTING_CATEGORY_SERIES.reduce((accumulator, series) => {
        accumulator[series.key] = buildReportingCategoryCounts(
          reportingCategoryRecords,
          series.fieldName,
        );
        return accumulator;
      }, {}),
    [reportingCategoryRecords],
  );

  const trainingCounts = useMemo(
    () => buildCounts(trainingRecords),
    [trainingRecords],
  );

  const readCounts = useMemo(() => buildCounts(readRecords), [readRecords]);

  const chartData = useMemo(
    () =>
      uniqueActionCards.map((card) => {
        const acNo = String(card.ac_no);
        const label =
          lang === "dk" ? card.danish_short_name : card.english_short_name;
        const reportingCategoryValues = REPORTING_CATEGORY_SERIES.reduce(
          (accumulator, series) => {
            accumulator[series.key] =
              showReportingCategoryBars &&
              reportingCategoryVisibility[series.key]
                ? reportingCategoryCounts[series.key]?.[acNo] ?? 0
                : 0;
            return accumulator;
          },
          {},
        );

        return {
          category: label,
          ac_no: acNo,
          english_name: card.english_name,
          danish_name: card.danish_name,
          default: hideDefaultBar ? 0 : defaultCounts[acNo] ?? 0,
          yellow: showIncidentTypeBars ? yellowCounts[acNo] ?? 0 : 0,
          red: showIncidentTypeBars ? redCounts[acNo] ?? 0 : 0,
          ...reportingCategoryValues,
          training: trainingCounts[acNo] ?? 0,
          reading: readCounts[acNo] ?? 0,
        };
      }),
    [
      uniqueActionCards,
      defaultCounts,
      yellowCounts,
      redCounts,
      reportingCategoryCounts,
      reportingCategoryVisibility,
      trainingCounts,
      readCounts,
      lang,
      hideDefaultBar,
      showIncidentTypeBars,
      showReportingCategoryBars,
    ],
  );

  useEffect(() => {
    cbRef.current?.(chartData);
  }, [chartData]);
  // ─────────────────────────────────────────────────────────────────────────

  const defaultLabel = lang === "dk" ? "Faktisk hændelse" : "Real Incident";
  const trainingLabel = lang === "dk" ? "Træning" : "Training";
  const readingLabel = lang === "dk" ? "Læsning" : "Reading";
  const yellowLabel = lang === "dk" ? "Gul" : "Yellow";
  const redLabel = lang === "dk" ? "Rød" : "Red";
  const legendDefault = lang === "dk" ? "Faktisk hændelse" : "Real Incident";
  const legendTraining = lang === "dk" ? "Træning" : "Training";
  const legendReading = lang === "dk" ? "Læst" : "Read";
  const legendYellow = lang === "dk" ? "Gul" : "Yellow";
  const legendRed = lang === "dk" ? "Rød" : "Red";
  const reportingCategoryLabels = useMemo(
    () =>
      REPORTING_CATEGORY_SERIES.reduce((accumulator, series) => {
        accumulator[series.key] = series.labels[lang] ?? series.labels.en;
        return accumulator;
      }, {}),
    [lang],
  );

  // Build bars array without any conditional hook calls
  const bars = [
    !hideDefaultBar ? (
      <Bar
        key="default"
        dataKey="default"
        fill={COLOR_DEFAULT}
        name={defaultLabel}
        radius={[6, 6, 0, 0]}
      >
        <LabelList
          dataKey="default"
          position="top"
          formatter={(v) => (v ? v : "")}
          style={BAR_VALUE_LABEL_STYLE}
        />
      </Bar>
    ) : null,
    showIncidentTypeBars && showIncidentYellow ? (
      <Bar
        key="yellow"
        dataKey="yellow"
        fill={COLOR_YELLOW}
        name={yellowLabel}
        radius={[6, 6, 0, 0]}
      >
        <LabelList
          dataKey="yellow"
          position="top"
          formatter={(v) => (v ? v : "")}
          style={BAR_VALUE_LABEL_STYLE}
        />
      </Bar>
    ) : null,
    showIncidentTypeBars && showIncidentRed ? (
      <Bar
        key="red"
        dataKey="red"
        fill={COLOR_RED}
        name={redLabel}
        radius={[6, 6, 0, 0]}
      >
        <LabelList
          dataKey="red"
          position="top"
          formatter={(v) => (v ? v : "")}
          style={BAR_VALUE_LABEL_STYLE}
        />
      </Bar>
    ) : null,
    ...REPORTING_CATEGORY_SERIES.map((series) =>
      showReportingCategoryBars && reportingCategoryVisibility[series.key] ? (
        <Bar
          key={series.key}
          dataKey={series.key}
          fill={series.color}
          name={reportingCategoryLabels[series.key]}
          radius={[6, 6, 0, 0]}
        >
          <LabelList
            dataKey={series.key}
            position="top"
            formatter={(v) => (v ? v : "")}
            style={BAR_VALUE_LABEL_STYLE}
          />
        </Bar>
      ) : null,
    ),
    showTraining ? (
      <Bar
        key="training"
        dataKey="training"
        fill={COLOR_TRAINING}
        name={trainingLabel}
        radius={[6, 6, 0, 0]}
      >
        <LabelList
          dataKey="training"
          position="top"
          formatter={(v) => (v ? v : "")}
          style={BAR_VALUE_LABEL_STYLE}
        />
      </Bar>
    ) : null,
    showRead ? (
      <Bar
        key="reading"
        dataKey="reading"
        fill={COLOR_READING}
        name={readingLabel}
        radius={[6, 6, 0, 0]}
      >
        <LabelList
          dataKey="reading"
          position="top"
          formatter={(v) => (v ? v : "")}
          style={BAR_VALUE_LABEL_STYLE}
        />
      </Bar>
    ) : null,
  ];

  const totalIncidents = defaultRecords.length;
  const totalYellow = Object.values(yellowCounts).reduce(
    (sum, value) => sum + Number(value ?? 0),
    0,
  );
  const totalRed = Object.values(redCounts).reduce(
    (sum, value) => sum + Number(value ?? 0),
    0,
  );
  const reportingCategoryTotals = useMemo(
    () =>
      REPORTING_CATEGORY_SERIES.reduce((accumulator, series) => {
        accumulator[series.key] = Object.values(
          reportingCategoryCounts[series.key] ?? {},
        ).reduce((sum, value) => sum + Number(value ?? 0), 0);
        return accumulator;
      }, {}),
    [reportingCategoryCounts],
  );
  const totalTraining = trainingRecords.length;
  const totalReading = readRecords.length;

  const summaryRows = useMemo(() => {
    const rows = [];
    if (!hideDefaultBar) {
      rows.push({
        label: legendDefault,
        value: totalIncidents,
        color: COLOR_DEFAULT,
        show: true,
      });
    }
    if (showIncidentTypeBars && showIncidentYellow) {
      rows.push({
        label: legendYellow,
        value: totalYellow,
        color: COLOR_YELLOW,
        show: true,
      });
    }
    if (showIncidentTypeBars && showIncidentRed) {
      rows.push({
        label: legendRed,
        value: totalRed,
        color: COLOR_RED,
        show: true,
      });
    }
    if (showReportingCategoryBars) {
      REPORTING_CATEGORY_SERIES.forEach((series) => {
        if (!reportingCategoryVisibility[series.key]) {
          return;
        }

        rows.push({
          label: reportingCategoryLabels[series.key],
          value: reportingCategoryTotals[series.key] ?? 0,
          color: series.color,
          show: true,
        });
      });
    }
    if (showTraining) {
      rows.push({
        label: legendTraining,
        value: totalTraining,
        color: COLOR_TRAINING,
        show: true,
      });
    }
    if (showRead) {
      rows.push({
        label: legendReading,
        value: totalReading,
        color: COLOR_READING,
        show: true,
      });
    }
    return rows;
  }, [
    hideDefaultBar,
    showIncidentTypeBars,
    showReportingCategoryBars,
    legendDefault,
    legendYellow,
    legendRed,
    legendTraining,
    legendReading,
    reportingCategoryLabels,
    reportingCategoryTotals,
    reportingCategoryVisibility,
    totalIncidents,
    totalYellow,
    totalRed,
    totalTraining,
    totalReading,
    showIncidentYellow,
    showIncidentRed,
    showTraining,
    showRead,
  ]);

  const tooltipContent = useMemo(
    () => makeTooltip({ rows: summaryRows, usePayloadValues: true }),
    [summaryRows],
  );
  return (
    <div className="risk-chart-shell" style={{ width: "100%", height }}>
      <ChartSummaryRows rows={summaryRows} />

      <ResponsiveContainer width="100%" height="86%">
        <BarChart data={chartData} margin={{ top: 24, right: 20, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
          <XAxis
            dataKey="category"
            interval={0}
            angle={-45}
            textAnchor="end"
            height={115}
            tick={{ fontSize: 14, fontWeight: 600 }}
          />
          <YAxis allowDecimals={false} />
          <Tooltip content={tooltipContent} />
          {bars.filter(Boolean)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RiskBarChart;
